/**
 * Model for BrandsCarousel components in Cypress tests
 */

type SupplierData = {
  'supplier.code': string
  'supplier.label'?: string
  media?: any[]
  // Add any other properties present in the supplier objects
}

type BrandsCarouselData = {
  title: string
  supplierCount: number
  brandsFirstRow: SupplierData[]
  brandsSecondRow: SupplierData[]
}

class BrandsCarousel {
  title: string

  supplierCount: number

  brandsFirstRow: SupplierData[]

  brandsSecondRow: SupplierData[]

  constructor(model: BrandsCarouselData) {
    this.title = model.title
    this.supplierCount = model.supplierCount
    this.brandsFirstRow = model.brandsFirstRow
    this.brandsSecondRow = model.brandsSecondRow

    // Bind methods to the instance
    this.getTotalBrandsCount = this.getTotalBrandsCount.bind(this)
    this.hasBrands = this.hasBrands.bind(this)
    this.getBrandNames = this.getBrandNames.bind(this)
    this.getBrandCodes = this.getBrandCodes.bind(this)
    this.hasBrand = this.hasBrand.bind(this)
  }

  /**
   * Get the total number of brands shown in the carousel
   *
   * @returns Number of brands in the carousel
   */
  getTotalBrandsCount(): number {
    return this.brandsFirstRow.length + this.brandsSecondRow.length
  }

  /**
   * Check if the carousel has any brands to display
   *
   * @returns Boolean indicating if carousel has brands
   */
  hasBrands(): boolean {
    return this.getTotalBrandsCount() > 0
  }

  /**
   * Get a list of all brand names in the carousel
   *
   * @returns Array of brand names
   */
  getBrandNames(): (string | undefined)[] {
    const firstRowNames = this.brandsFirstRow.map((brand) => brand['supplier.label'])
    const secondRowNames = this.brandsSecondRow.map((brand) => brand['supplier.label'])

    return [...firstRowNames, ...secondRowNames]
  }

  /**
   * Get a list of all brand codes in the carousel
   *
   * @returns Array of brand codes
   */
  getBrandCodes(): string[] {
    const firstRowCodes = this.brandsFirstRow.map((brand) => brand['supplier.code'])
    const secondRowCodes = this.brandsSecondRow.map((brand) => brand['supplier.code'])
    return [...firstRowCodes, ...secondRowCodes]
  }

  /**
   * Check if a specific brand is present in the carousel
   *
   * @param brandName The name of the brand to check
   * @returns Boolean indicating if the brand is present
   */
  hasBrand(brandName: string): boolean {
    return this.getBrandNames().includes(brandName)
  }
}

export default BrandsCarousel
